import { Alert, Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getGrowthRoas } from 'API/engagement';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { RoasRow } from 'types/engagement.types';
import { fmtDec, fmtEuroCents, metricStatusLabel } from '../../shared/format';
import { MetricText } from '../../shared/metric';
import { KpiRow, KpiTile, QueryState, SectionCard } from '../../shared/ui';
import { ratioMetric, sumMetric } from '../engagement-logic';

export const RoasTable: React.FC<{ rows: RoasRow[] }> = ({ rows }) => {
  const { t, i18n } = useTranslation();
  const euro = fmtEuroCents(i18n.language);
  const dec = fmtDec(i18n.language, 1);
  const name = (r: RoasRow) => {
    if (r.campaignId === 'direct' || r.platform === 'direct') return t('engSourceDirect');
    if (r.campaignId === 'unattributed' || r.platform === 'unattributed') return t('engSourceUnattributed');
    return `${r.platform === 'meta' ? 'Meta' : r.platform === 'google' ? 'Google' : r.platform} · ${r.name}`;
  };
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size={'small'}>
        <TableHead>
          <TableRow>
            <TableCell>{t('engColAdCampaign')}</TableCell>
            <TableCell align={'right'}>{t('engColSpend')}</TableCell>
            <TableCell align={'right'}>{t('engColPurchases')}</TableCell>
            <TableCell align={'right'}>{t('engColRevenueToDate')}</TableCell>
            <TableCell align={'right'}>{t('engColRoasToDate')}</TableCell>
            <TableCell align={'right'}>{t('engColRoas12m')}</TableCell>
            <TableCell align={'right'}>{t('engColPayback')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.platform}-${r.campaignId}`}>
              <TableCell>{name(r)}</TableCell>
              <TableCell align={'right'}>
                <MetricText value={r.spendCents} format={euro} />
              </TableCell>
              <TableCell align={'right'}>{r.purchases}</TableCell>
              <TableCell align={'right'}>
                <MetricText value={r.revenueCents} format={euro} />
              </TableCell>
              <TableCell align={'right'}>
                <MetricText value={r.roasToDate} format={dec} />
              </TableCell>
              <TableCell align={'right'}>
                <MetricText value={r.roas12m} format={dec} />
              </TableCell>
              <TableCell align={'right'}>
                <MetricText value={r.paybackMonths} format={(n) => t('engMonthsShort', { value: dec(n) })} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const RevenueTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const roas = useQuery({ queryKey: ['engagement', 'growth', 'roas', 30], queryFn: () => getGrowthRoas(30) });
  const rows = roas.data?.rows ?? [];
  const paidRows = rows.filter((r) => r.platform === 'meta' || r.platform === 'google');
  // The API's totals carry the spend status ("not connected" is never a silent 0 €).
  const totals = roas.data?.totals;
  const spend = totals?.spend ?? sumMetric(paidRows, 'spendCents');
  const revenue = totals ? { value: totals.revenueCents, status: 'ok' as const } : sumMetric(paidRows, 'revenueCents');
  const purchases = paidRows.reduce((a, r) => a + r.purchases, 0);

  return (
    <Stack gap={2.5}>
      <QueryState isLoading={roas.isLoading} error={roas.error}>
        {roas.data?.status && roas.data.status !== 'ok' ? (
          <Alert severity={roas.data.status === 'failed' ? 'error' : 'info'} data-testid={'roas-status'}>
            {metricStatusLabel(roas.data.status, t)}
            {roas.data.reason ? ` — ${roas.data.reason}` : ''}
          </Alert>
        ) : null}
        <KpiRow>
          <KpiTile label={t('engKpiAdSpend')} value={<MetricText value={spend} format={fmtEuroCents(lng)} variant={'kpi'} />} sub={t('engKpiAdSpendSub')} />
          <KpiTile
            label={t('engKpiRevenueFromAds')}
            value={<MetricText value={revenue} format={fmtEuroCents(lng)} variant={'kpi'} />}
            sub={t('engKpiPurchasesCount', { count: purchases })}
          />
          <KpiTile label={t('engColRoasToDate')} value={<MetricText value={ratioMetric(revenue, spend)} format={fmtDec(lng, 1)} variant={'kpi'} />} sub={t('engKpiRoasTarget')} />
        </KpiRow>
        <SectionCard title={t('engRoasTitle')}>
          {rows.length ? <RoasTable rows={rows} /> : <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('engRoasEmpty')}</Typography>}
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5 }}>{t('engRoasNote')}</Typography>
        </SectionCard>
      </QueryState>
    </Stack>
  );
};

export default RevenueTab;
